import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldLabel } from './FieldLabel'
import { SELECTABLE_USER_STATES } from './UserStateSelect'
import { OrdersApi, type Order } from '@/services/orders/ordersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { nextCopyName } from '@/utils/copyName'

export interface OrderCopyDialogProps {
  order: Order
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Stands in for "leave each line item in its own state" — a Select needs a value. */
const SAME_STATE = '__same__'

/** Ids are client-generated, matching how the rest of the app creates objects. */
function newOrderId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
}

/**
 * Copies an order, as the old UI's copy dialog does: a name suggested from the
 * original, and what comes along with it — its line items, their targeting and
 * creatives, and the order's budgets.
 */
export function OrderCopyDialog({ order, open, onOpenChange }: OrderCopyDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [lineItemUserState, setLineItemUserState] = useState(SAME_STATE)
  const [copyLineItems, setCopyLineItems] = useState(true)
  const [copyTargeting, setCopyTargeting] = useState(true)
  const [copyCreatives, setCopyCreatives] = useState(true)
  const [copyBudgets, setCopyBudgets] = useState(true)
  const [goToCopy, setGoToCopy] = useState(true)

  const hasBudgets = (order.budget ?? []).length > 0

  // Opening starts from the order as it stands, however it was left last time.
  useEffect(() => {
    if (!open) return
    setName(nextCopyName(order.name))
    setLineItemUserState(SAME_STATE)
  }, [open, order.name])

  const copy = useMutation({
    mutationFn: () =>
      OrdersApi.copy(order.id, {
        newId: newOrderId(),
        name: name.trim(),
        advertiserId: order.advertiserId,
        lineItemUserState: lineItemUserState === SAME_STATE ? undefined : lineItemUserState,
        copyLineItems,
        copyTargeting,
        copyCreatives,
        copyBudgets,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['advertiser-orders'] })
      toast.success(t('orders.copied', { name: created.name }))
      onOpenChange(false)
      if (goToCopy) navigate(`/advertising/orders/${created.id}`)
    },
    onError: (error) =>
      toast.error(t('orders.copyFailed'), { description: apiErrorDescription(error) }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('orders.copyTitle')}</DialogTitle>
          <DialogDescription>{order.name}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t('common.name')}</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-2">
            <FieldLabel>{t('orders.copyInclude')}</FieldLabel>
            <Label className="font-normal">
              <Checkbox
                checked={copyLineItems}
                onCheckedChange={(value) => setCopyLineItems(!!value)}
              />
              {t('nav.lineItems')}
            </Label>
            {/* Targeting and creatives belong to the line items, so they only
                mean something when those come along. */}
            <Label className="font-normal">
              <Checkbox
                checked={copyTargeting}
                disabled={!copyLineItems}
                onCheckedChange={(value) => setCopyTargeting(!!value)}
              />
              {t('targeting.title')}
            </Label>
            <Label className="font-normal">
              <Checkbox
                checked={copyCreatives}
                disabled={!copyLineItems}
                onCheckedChange={(value) => setCopyCreatives(!!value)}
              />
              {t('creatives.title')}
            </Label>
            {hasBudgets && (
              <Label className="font-normal">
                <Checkbox
                  checked={copyBudgets}
                  onCheckedChange={(value) => setCopyBudgets(!!value)}
                />
                {t('orders.copyBudgets')}
              </Label>
            )}
          </div>

          {copyLineItems && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel>{t('orders.copyLineItemState')}</FieldLabel>
              <Select value={lineItemUserState} onValueChange={setLineItemUserState}>
                <SelectTrigger>
                  <SelectValue>
                    {lineItemUserState === SAME_STATE
                      ? t('orders.copyLineItemStateSame')
                      : t(`deliveryStatuses.${lineItemUserState}`, { defaultValue: lineItemUserState })}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SAME_STATE}>
                    {t('orders.copyLineItemStateSame')}
                  </SelectItem>
                  {SELECTABLE_USER_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {t(`deliveryStatuses.${state}`, { defaultValue: state })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Label className="font-normal">
            <Checkbox checked={goToCopy} onCheckedChange={(value) => setGoToCopy(!!value)} />
            {t('orders.copyGoTo')}
          </Label>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            loading={copy.isPending}
            disabled={!name.trim()}
            onClick={() => copy.mutate()}
          >
            <Copy className="size-4" />
            {t('common.copy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
